import * as zod from 'zod';

import { CLICKFIND_MAX_MATCH_AREA_PX2 } from '@sb/dinofast';
import { CartesianPose } from '@sb/geometry';
import { ErrorCode } from '@sb/messenger/auto_generated/messenger/vision_bot_service';

// DinoFastPrecision and DinoFastReferenceEmbedding are used locally by
// DinoFastParams below.
import {
  Detection3DPoseEstimationConfig,
  DinoFastPrecision,
  DinoFastReferenceEmbedding,
} from './visionConfigSchemas';

// Re-exported from the geometry-free `visionConfigSchemas` leaf so the
// skill-config layer (`@sb/ai`) can consume these primitives without dragging
// in `@sb/geometry`/`three`. See visionConfigSchemas.ts for the rationale.
export {
  Detection3DPoseEstimationConfig,
  DinoFastPrecision,
  DinoFastReferenceEmbedding,
  ResultOrdering,
} from './visionConfigSchemas';

// blob 2D detection method

export const Blob2D = zod.object({
  score: zod.number(),
  contour: zod
    .array(
      zod.object({
        x: zod.number(),
        y: zod.number(),
      }),
    )
    .default([]),
  meanIntensity: zod.number().optional(),
  circularity: zod.number().optional(),
  area: zod.number().optional(),
  classId: zod.number().optional(),
  className: zod.string().optional(),
  maskRle: zod.instanceof(Uint8Array).optional(),
  maskWidth: zod.number().optional(),
  maskHeight: zod.number().optional(),
});

export type Blob2D = zod.infer<typeof Blob2D>;

export const Detection2D = zod.object({
  x: zod.number(),
  y: zod.number(),
  width: zod.number(),
  height: zod.number(),
  rotation: zod.number(), // radians
  score: zod.number(),
  classId: zod.number().optional(),
  className: zod.string().optional(),
});

export type Detection2D = zod.infer<typeof Detection2D>;

export const LocateMatch = zod.object({
  detection: Detection2D,
  segment: Blob2D.nullable().default(null),
});

export type LocateMatch = zod.infer<typeof LocateMatch>;

export const Extents3D = zod.object({
  x: zod.number(),
  y: zod.number(),
  z: zod.number(),
});

export type Extents3D = zod.infer<typeof Extents3D>;

// The locate result carries the detector BOX (Detection2D), the resolved 6D
// pose, and — when segmentation ran — the SAM2 `segment` (contour) echoed back
// from vision-bot's plane-projection response. Consumers read
// `.blob.{x,y,width,height,rotation}` for the box and `.segment.contour` for the
// outline (null when segmentation was off or produced no contour).
export const LocateResult = zod.object({
  blob: Detection2D,
  pose: CartesianPose,
  extents: Extents3D.optional(),
  segment: Blob2D.nullable().default(null),
});

export type LocateResult = zod.infer<typeof LocateResult>;

export const Blob2DParams = zod.object({
  minArea: zod.number().default(1000),
  maxArea: zod.number().default(10000),
  minThreshold: zod.number().default(50),
  maxThreshold: zod.number().default(220),
  minCircularity: zod.number().default(0.1),
  maxCircularity: zod.number().default(1),
  filterByCircularity: zod.boolean().default(false),
  invertImage: zod.boolean().default(false),
  disableRotation: zod.boolean().default(false),
});

export type Blob2DParams = zod.infer<typeof Blob2DParams>;

export const BlobDetection2DMethod = zod.object({
  kind: zod.literal('BlobDetection2D'),
  settings: Blob2DParams,
});

export type BlobDetection2DMethod = zod.infer<typeof BlobDetection2DMethod>;

// Shape 2D detection method

export const Shape2DParams = zod.object({
  minScale: zod.number().default(0.8),
  maxScale: zod.number().default(1.2),
  maxRotation: zod.number().default(180),
  matchThreshold: zod.number().default(0.5),
  boundingBox: zod
    .object({
      rotationDegrees: zod.number().default(0),
      scaleX: zod.number().default(1),
      scaleY: zod.number().default(1),
    })
    .default({}),
  filterIntersections: zod.boolean().default(true),
  disableRotation: zod.boolean().default(false),
  rejectOnEdge: zod.boolean().default(false),
});

export type Shape2DParams = zod.infer<typeof Shape2DParams>;

export const TemplateImage = zod.object({
  storageID: zod.string(),
});

export type TemplateImage = zod.infer<typeof TemplateImage>;

export const ShapeDetection2DMethod = zod.object({
  kind: zod.literal('ShapeDetection2D'),
  templateImage: TemplateImage,
  settings: Shape2DParams,
});

export type ShapeDetection2DMethod = zod.infer<typeof ShapeDetection2DMethod>;

// Chessboard detection method
export const ChessboardDetection2DMethod = zod.object({
  kind: zod.literal('ChessboardDetection2D'),
});

export type ChessboardDetection2DMethod = zod.infer<
  typeof ChessboardDetection2DMethod
>;

// Text prompt detection method
export const TextPromptDetection2DParams = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  textInput: zod.string().default(''),
  useSam: zod.boolean().default(false),
  confidenceThreshold: zod.number().default(0.35),
});

export type TextPromptDetection2DParams = zod.infer<
  typeof TextPromptDetection2DParams
>;

export const TextPromptDetection2DMethod = zod.object({
  kind: zod.literal('TextPromptDetection2D'),
  settings: TextPromptDetection2DParams,
});

export type TextPromptDetection2DMethod = zod.infer<
  typeof TextPromptDetection2DMethod
>;

export const CustomCheckpointModelType = zod.enum([
  'YoloV11',
  'YoloV8-OBB',
  'RF-DETR-Nano',
  'RF-DETR-Small',
  'RF-DETR-Medium',
  'RF-DETR-Large',
]);

export const CustomCheckpointDetection2DParams = zod.object({
  gpuTaskRunnerId: zod.string(),
  // Checkpoint fields stay required (the skill config defaults them, so a
  // Roboflow-source method still carries modelPath='' etc.) — keeping them
  // required avoids widening the type for existing settings consumers.
  modelPath: zod.string(),
  modelType: CustomCheckpointModelType,
  thresholdingParams: zod.record(zod.string(), zod.string()),
  // Model source: 'checkpoint' (a runner-hosted checkpoint file) or 'roboflow'
  // (a Roboflow-hosted model run via the GPU task runner). Optional (not
  // defaulted) so it isn't forced into callers that build settings literals;
  // absent is treated as 'checkpoint' at runtime.
  modelSource: zod.enum(['checkpoint', 'roboflow']).optional(),
  // Roboflow-source fields (present when modelSource === 'roboflow').
  // roboflowId is the model url "<workspace>/<project>/<version>".
  roboflowId: zod.string().optional(),
  roboflowWorkspaceName: zod.string().optional(),
});

export type CustomCheckpointDetection2DParams = zod.infer<
  typeof CustomCheckpointDetection2DParams
>;

export const CustomCheckpointDetection2DMethod = zod.object({
  kind: zod.literal('CustomCheckpointDetection2D'),
  settings: CustomCheckpointDetection2DParams,
});

export type CustomCheckpointDetection2DMethod = zod.infer<
  typeof CustomCheckpointDetection2DMethod
>;

// DinoFast detection method
// (DinoFastPrecision and DinoFastReferenceEmbedding are defined in
// visionConfigSchemas and re-exported above.)

// Serialized weights for the learned locate scorer.
// Trained server-side by vision-bot. When present this replaces the cosine
// scorer; when null, locate falls back to cosine. `kind` selects the model:
// 'mlp' (single-hidden-layer net) or 'logistic' (linear regression).
const DinoFastMlpHeadWeights = zod.object({
  kind: zod.literal('mlp'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  hidden: zod.number().int(),
  w1: zod.array(zod.number()), // hidden * inputDim, row-major [h][i]
  b1: zod.array(zod.number()), // hidden
  w2: zod.array(zod.number()), // hidden
  b2: zod.number(),
});

const DinoFastLogisticHeadWeights = zod.object({
  kind: zod.literal('logistic'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  w: zod.array(zod.number()), // inputDim
  b: zod.number(),
});

export const DinoFastLocateHead = zod.discriminatedUnion('kind', [
  DinoFastMlpHeadWeights,
  DinoFastLogisticHeadWeights,
]);

export type DinoFastLocateHead = zod.infer<typeof DinoFastLocateHead>;

// embeddingModelVersion is a free-form string (not a zod.literal) so saved
// configs survive a deployment-time encoder swap and the wizard can surface
// the mismatch as a recoverable error rather than failing to deserialize.
//
// scoreThreshold gates the heatmap and is interpreted against whichever scorer
// produced it: cosine similarity (no locateHead) or the learned head's sigmoid
// output (locateHead set). The schema bound is [-2, 2] to admit any legacy cosine config
// — the cosine effective range with the hard-coded 0.8 negative weight is
// [-1.8, 1.8], and sigmoid scores are [0, 1] — but the teach-UI slider has
// always constrained user-set thresholds to [0, 1], which is sensible for both
// scorers, and the teach modal previews blobs live against the trained head so
// the threshold is calibrated against the actual scoring function.
export const DinoFastParams = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  embeddingModelVersion: zod.string().default('dinov3-vits16-v1'),
  embeddingDim: zod.number().int().default(384),
  precision: DinoFastPrecision.default('standard'),
  positives: zod.array(DinoFastReferenceEmbedding).default([]),
  negatives: zod.array(DinoFastReferenceEmbedding).default([]),
  // Learned scorer trained in the teach UI. Null on legacy/untaught steps,
  // which fall back to cosine scoring.
  locateHead: DinoFastLocateHead.nullable().default(null),
  scoreThreshold: zod.number().min(-2).max(2).default(0.5),
  // Minimum match area in original-image pixel² space. runLocate filters on the
  // match's final pixel shape (contour when segmented, else the detector box).
  minMatchAreaPx2: zod.number().min(0).default(2000),
  // Maximum match area in original-image pixel² space; a value at/above
  // CLICKFIND_MAX_MATCH_AREA_PX2 means "no upper limit". Defaults to the ceiling
  // so legacy configs stay unbounded.
  maxMatchAreaPx2: zod.number().min(0).default(CLICKFIND_MAX_MATCH_AREA_PX2),
  nmsIouThreshold: zod.number().min(0).max(1).default(0.3),
  maxBlobs: zod.number().int().min(1).default(20),
  // When true (the default), each detected bbox prompts a SAM2 segmentation
  // pass that returns a contour-only segment (pixel-accurate mask) attached to
  // the match; the detector box stays the raw DinoFast OBB. When false, SAM2 is
  // skipped and the match carries no segment. Decoupled from `precision` (which
  // only controls the DINO encoder grid resolution), so any precision can run
  // with or without segmentation. Defaults to true to preserve the prior
  // standard/fine behavior, which always ran SAM2.
  enableSegmentationMask: zod.boolean().default(true),
  // Which segmentation backend refines DinoFast OBBs into masks when
  // `enableSegmentationMask` is on. 'sam2' is the prompt-based segmenter;
  // 'guided_filter' and 'bilateral_solver' are CPU-only edge-aware refiners
  // of the DINO heatmap. Only consulted when enableSegmentationMask is true.
  segmentationMethod: zod
    .enum(['sam2', 'guided_filter', 'bilateral_solver', 'heatmap_threshold'])
    .default('heatmap_threshold'),
});

export type DinoFastParams = zod.infer<typeof DinoFastParams>;

export const DinoFastMethod = zod.object({
  kind: zod.literal('DinoFast'),
  settings: DinoFastParams,
});

export type DinoFastMethod = zod.infer<typeof DinoFastMethod>;

export const ManualBoundingBoxParams = zod.object({
  cx: zod.number().default(640),
  cy: zod.number().default(360),
  width: zod.number().default(100),
  height: zod.number().default(100),
});

export type ManualBoundingBoxParams = zod.infer<typeof ManualBoundingBoxParams>;

export const ManualBoundingBoxMethod = zod.object({
  kind: zod.literal('ManualBoundingBox'),
  settings: ManualBoundingBoxParams,
});

export type ManualBoundingBoxMethod = zod.infer<typeof ManualBoundingBoxMethod>;

// 3D detection method (text-prompt 2D detection -> SAM2 masks -> 3D pose).
// The 3D-pose-estimation fields (and their rationale comments) live in the
// shared `Detection3DPoseEstimationConfig` in visionConfigSchemas.ts (also
// consumed by `@sb/ai`'s skill projection). Here we add the routine-step's
// 2D-detection fields and re-assert the routine-step defaults (OBB_ICP/PRIMITIVE).
export const Detection3DParams = Detection3DPoseEstimationConfig.extend({
  gpuTaskRunnerId: zod.string().default(''),
  // 2D detection settings (text prompt → SAM2 masks → 3D pose)
  textPrompt: zod.string().default(''),
  boxThreshold: zod.number().default(0.35),
  textThreshold: zod.number().default(0.25),
  // Re-assert routine-step defaults (differ from shared schema's no-CAD defaults)
  poseMethod: zod.enum(['OBB_DEPTH', 'OBB_ICP', 'FPFH_ICP']).default('OBB_ICP'),
  cadSource: zod.enum(['NONE', 'PRIMITIVE', 'CAD_FILE']).default('PRIMITIVE'),
});

export type Detection3DParams = zod.infer<typeof Detection3DParams>;

export const Detection3DMethod = zod.object({
  kind: zod.literal('Detection3D'),
  settings: Detection3DParams,
});

export type Detection3DMethod = zod.infer<typeof Detection3DMethod>;

// Locate method union

export const LocateMethod = zod.discriminatedUnion('kind', [
  BlobDetection2DMethod,
  ShapeDetection2DMethod,
  ChessboardDetection2DMethod,
  TextPromptDetection2DMethod,
  CustomCheckpointDetection2DMethod,
  ManualBoundingBoxMethod,
  Detection3DMethod,
  DinoFastMethod,
]);

export type LocateMethod = zod.infer<typeof LocateMethod>;

// GPUTaskRunner vision inference response types

export const CheckpointBasedInferenceErrorCode = zod.nativeEnum(ErrorCode);

export type CheckpointBasedInferenceErrorCode = zod.infer<
  typeof CheckpointBasedInferenceErrorCode
>;

export const CheckpointBasedInferenceError = zod.object({
  code: CheckpointBasedInferenceErrorCode,
  message: zod.string(),
});

export type CheckpointBasedInferenceError = zod.infer<
  typeof CheckpointBasedInferenceError
>;

export const CheckpointBasedInferenceResult = zod.object({
  // Detector box + optional SAM2 segment. Matches the proto wire shape
  // (CheckpointBasedInferenceResult.blobs is LocateMatch[]) so the GPU
  // producers' safeParse accepts it: box → detection, contour/mask → segment.
  blobs: zod.array(LocateMatch).default([]),
  processingTimeMs: zod.number().optional(),
  modelPath: zod.string().optional(),
});

export type CheckpointBasedInferenceResult = zod.infer<
  typeof CheckpointBasedInferenceResult
>;

export const RunCheckpointBasedInferenceResponse = zod
  .object({
    data: CheckpointBasedInferenceResult,
    error: CheckpointBasedInferenceError,
  })
  .partial();
export type RunCheckpointBasedInferenceResponse = zod.infer<
  typeof RunCheckpointBasedInferenceResponse
>;

// Result of a Roboflow object-detection run. vision-bot maps the detector's
// predictions into LocateMatch (box only, segment unset), so this feeds the
// same plane-projection pipeline as checkpoint inference.
export const RoboflowDetectionResult = zod.object({
  blobs: zod.array(LocateMatch),
  processingTimeMs: zod.number().optional(),
});
export type RoboflowDetectionResult = zod.infer<typeof RoboflowDetectionResult>;

export const RunRoboflowDetectionResponse = zod
  .object({
    data: RoboflowDetectionResult,
    error: CheckpointBasedInferenceError,
  })
  .partial();
export type RunRoboflowDetectionResponse = zod.infer<
  typeof RunRoboflowDetectionResponse
>;
